/**
 * Recurring failures and pending actions carried over from the audits before this one.
 *
 * Two counts open two panels, and a row inside either opens its own recorded facts. The
 * section owns which panel is open and which row's facts are showing.
 */
import { useState } from 'react';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import {
  MessageBanner,
  MessageBannerType,
} from '@amzn/stencil-react-components/message-banner';
import { Text } from '@amzn/stencil-react-components/text';
import { IconInformationFillSmall, IconRefreshSmall } from '@amzn/stencil-react-icons';

import type { OpenTaskRef, RecurringFailures } from 'src/types/starlight/AuditInsights';
import type { Messages } from 'src/v2/shared/i18n/useMessages';
import { INTERACTION_TYPES, trackInteraction } from 'src/v2/shared/observability';
import { DocumentDisplay } from 'src/v2/shared/ui/DocumentDisplay';
import { AUDIT_INSIGHTS_ACTIONS, AUDIT_INSIGHTS_PAGES } from '../tracking';
import type { AuditInsightsMessageKey } from '../translations';
import { buildDesktopTaskDetailPath } from 'src/v2/shared/routing';
import {
  openTaskDetail,
  recurringDetail,
  type InsightDetail,
  type OpenTaskContext,
} from '../logic/insightDetail';
import { SectionHeading } from './SectionHeading';
import { InsightTile } from './InsightTile';
import { DetailPanel } from './DetailPanel';
import { PLACEHOLDER_BOX } from './panelEdges';
import { InsightDetailModal } from './InsightDetailModal';
import { OpenTaskRow, RecurringFailureRow } from './InsightRows';
import { taskStatusLabel } from './taskStatus';

/**
 * The two panels a tile can open. Declared once as a const map so the ids
 * cannot drift between the type and the code that compares against it.
 */
const PANEL = {
  recurring: 'recurring',
  openTasks: 'openTasks',
} as const;

type PanelId = (typeof PANEL)[keyof typeof PANEL];

/** Which panel the reader has open. Only one opens at a time, as in the prototype. */
type OpenPanel = PanelId | null;

/** Names each panel separately so follow-up on recurrence and on open work are distinct. */
function panelAction(panel: PanelId): string {
  return panel === PANEL.recurring
    ? AUDIT_INSIGHTS_ACTIONS.openRecurringPanel
    : AUDIT_INSIGHTS_ACTIONS.openOpenTasksPanel;
}

/**
 * Which panel is open before the reader touches anything.
 *
 * Recurrence leads, because it is the finding the section exists to surface. It is only
 * offered when there is an earlier audit to compare against, so without history the open
 * work takes its place, and with neither nothing opens.
 */
function defaultPanel(hasHistory: boolean, hasTaskInfo: boolean): OpenPanel {
  if (hasHistory) {
    return PANEL.recurring;
  }
  return hasTaskInfo ? PANEL.openTasks : null;
}

interface RecurringSectionProps {
  /** Scopes the link out to a task's own page. */
  businessUnitId: string;
  recurringFailures: RecurringFailures | undefined;
  openTasks: OpenTaskRef[];
  /** True when the task read failed, so the count may undercount. */
  openTasksUnavailable: boolean;
  /** What each question asked, keyed by id. The insight carries titles but not wording. */
  questionTextById: ReadonlyMap<string, string>;
  messages: Messages<AuditInsightsMessageKey>;
}

/**
 * What this audit repeated from earlier audits at the same site, and what is still
 * outstanding against it.
 *
 * Recurrence needs an earlier audit to compare against. When there is none the counts would
 * both be a meaningless zero, so the section says so instead of showing them.
 */
export function RecurringSection({
  businessUnitId,
  recurringFailures,
  openTasks,
  openTasksUnavailable,
  questionTextById,
  messages,
}: RecurringSectionProps): JSX.Element | null {
  const recurringItems = recurringFailures?.items ?? [];
  const hasTaskInfo = openTasks.length > 0 || openTasksUnavailable;
  const hasHistory = recurringFailures?.hasPriorHistory ?? false;

  // Computed once, on mount. Sound because the parent renders this section only once the
  // insight has arrived, so the counts behind the tiles are already known.
  const [openPanel, setOpenPanel] = useState<OpenPanel>(() =>
    defaultPanel(hasHistory, hasTaskInfo),
  );
  // One modal serves the whole list, so the open row is held as its facts rather than as
  // an index into whichever list is showing.
  const [openDetail, setOpenDetail] = useState<InsightDetail | undefined>(undefined);
  const { t, f } = messages;

  if (!recurringFailures && !hasTaskInfo) {
    return null;
  }

  return (
    <Col gridGap="dimensions.spacing.200">
      <SectionHeading
        icon={<IconRefreshSmall />}
        label={t('recurringTitle')}
        badge={t('recurringContext')}
        badgeAlign="end"
      />
      {hasHistory || hasTaskInfo ? renderTiles() : renderNoHistory()}
    </Col>
  );

  function renderNoHistory(): JSX.Element {
    return (
      <Row
        backgroundColor="color.surface.bg-muted"
        borderRadius="dimensions.border.radius.200"
        padding="dimensions.spacing.300"
        gridGap="dimensions.spacing.200"
        alignItems="flex-start"
        css={PLACEHOLDER_BOX}
      >
        <IconInformationFillSmall color="color.text.secondary" aria-hidden />
        <Col gridGap="dimensions.spacing.50">
          <Text fontSize="T100" fontWeight="medium">
            {t('noPriorHistoryTitle')}
          </Text>
          <Text fontSize="T100" color="color.text.secondary">
            {t('noPriorHistoryText')}
          </Text>
        </Col>
      </Row>
    );
  }

  function renderTiles(): JSX.Element {
    return (
      <Col gridGap="dimensions.spacing.200">
        <Row gridGap="dimensions.spacing.200" flexWrap="wrap">
          {/* A recurring count only means something once there is an earlier audit. */}
          {hasHistory ? (
            <InsightTile
              count={recurringItems.length}
              label={t('tileRecurring')}
              subLabel={t('tileRecurringSub')}
              tone="critical"
              isSelected={openPanel === PANEL.recurring}
              onClick={() => togglePanel(PANEL.recurring)}
            />
          ) : null}
          <InsightTile
            count={openTasks.length}
            label={t('tileOpenTasks')}
            subLabel={t('tileOpenTasksSub')}
            tone="warning"
            isSelected={openPanel === PANEL.openTasks}
            onClick={() => togglePanel(PANEL.openTasks)}
          />
        </Row>
        {openTasksUnavailable ? (
          <MessageBanner type={MessageBannerType.Warning} isDismissible={false}>
            {t('openTasksUnavailable')}
          </MessageBanner>
        ) : null}
        {renderPanel()}
        <InsightDetailModal
          detail={openDetail}
          readOnlyLabel={t('detailReadOnly')}
          closeLabel={t('detailClose')}
          viewLabel={t('openTaskView')}
          onClose={closeDetail}
        />
      </Col>
    );
  }

  function renderPanel(): JSX.Element | null {
    if (openPanel === PANEL.recurring) {
      return (
        <DetailPanel
          icon={<IconRefreshSmall />}
          title={t('panelRecurringTitle')}
          emptyLabel={t('panelRecurringEmpty')}
          hint={t('panelRecurringHint')}
        >
          {recurringItems.map((item) => (
            <RecurringFailureRow
              key={item.questionId}
              item={item}
              formatters={{ f }}
              onOpen={() =>
                openRowDetail(
                  recurringDetail(item, { f }, questionTextById.get(item.questionId)),
                  AUDIT_INSIGHTS_ACTIONS.openRecurringDetail,
                )
              }
            />
          ))}
        </DetailPanel>
      );
    }

    if (openPanel === PANEL.openTasks) {
      return (
        <DetailPanel
          icon={<DocumentDisplay />}
          title={t('panelOpenTasksTitle')}
          emptyLabel={t('panelOpenTasksEmpty')}
          hint={t('panelOpenTasksHint')}
        >
          {openTasks.map((task) => (
            <OpenTaskRow
              key={task.taskId}
              task={task}
              formatters={{ f }}
              viewHref={buildDesktopTaskDetailPath(businessUnitId, task.taskId)}
              viewLabel={t('openTaskView')}
              onOpen={() =>
                openRowDetail(
                  openTaskDetail(task, { f }, taskContext(task)),
                  AUDIT_INSIGHTS_ACTIONS.openOpenTaskDetail,
                )
              }
            />
          ))}
        </DetailPanel>
      );
    }

    return null;
  }

  function togglePanel(panel: PanelId): void {
    const isClosing = openPanel === panel;
    setOpenPanel(isClosing ? null : panel);
    trackInteraction({
      type: INTERACTION_TYPES.actionTriggered,
      page: AUDIT_INSIGHTS_PAGES.tab,
      action: isClosing ? AUDIT_INSIGHTS_ACTIONS.closePanel : panelAction(panel),
    });
  }

  /** Opens one row's recorded facts. The action names which list the row came from. */
  function openRowDetail(detail: InsightDetail, action: string): void {
    setOpenDetail(detail);
    trackInteraction({
      type: INTERACTION_TYPES.actionTriggered,
      page: AUDIT_INSIGHTS_PAGES.tab,
      action,
    });
  }

  /** What the detail builder cannot work out for itself: the label and the task's page. */
  function taskContext(task: OpenTaskRef): OpenTaskContext {
    return {
      statusLabel: taskStatusLabel(task.taskStatus),
      viewHref: buildDesktopTaskDetailPath(businessUnitId, task.taskId),
    };
  }

  function closeDetail(): void {
    setOpenDetail(undefined);
    trackInteraction({
      type: INTERACTION_TYPES.actionTriggered,
      page: AUDIT_INSIGHTS_PAGES.tab,
      action: AUDIT_INSIGHTS_ACTIONS.closeDetail,
    });
  }
}
